import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useContent } from '../../content';
import ScoreRadar from '../../components/charts/ScoreRadar';
import { useMyPageDashboard } from '../../hooks/useMyPageDashboard';
import ChugumiMatrix from '../../components/charts/ChugumiMatrix';

// Components - Updated translations for Beyond Test titles
import StatusGrid from '../../components/mypage/StatusGrid';
import ReportCard from '../../components/mypage/ReportCard';
import FaceAnalysisCard from '../../components/mypage/FaceAnalysisCard';
import EmptyState from '../../components/mypage/EmptyState';
import { getMyPageCardState } from './getMyPageCardState';
import { SEB_TEST_DATA } from '../../data/sebTestData';
import { formatResultDate } from '../../utils/dateFormat';
import { localizeChemistryResult } from '../../i18n/chemistryResult';

// desc에서 핵심 요약 문장 추출 ("당신이 원하는 사랑은" 다음 줄)
const extractShortDesc = (desc) => {
    if (!desc || typeof desc !== 'string') return '';
    const lines = desc.split('\n').map(l => l.trim()).filter(Boolean);
    const keyIdx = lines.findIndex(l => l.includes('당신이 원하는 사랑은'));
    if (keyIdx !== -1 && lines[keyIdx + 1]) return lines[keyIdx + 1];
    return lines[lines.length - 1] || '';
};

// BeyondCard: Beyond 테스트 결과를 직관적으로 표시하는 서브 컴포넌트
const DateStamp = ({ value }) => {
    const label = formatResultDate(value);
    if (!label) return null;
    return <div style={styles.resultDate}>{label}</div>;
};

const getChemistryLevel = (score, midThreshold = 45) => {
    const numericScore = Number(score);
    if (!Number.isFinite(numericScore)) return null;
    if (numericScore >= 70) return { label: 'HIGH', color: '#ff6b8a' };
    if (numericScore >= midThreshold) return { label: 'MID', color: '#fbbf24' };
    return { label: 'LOW', color: '#64748b' };
};

const BeyondCard = ({ beyond, getC, styles, t }) => {
    const summary = beyond?.summary || {};
    const sCode = summary.S || 'M';
    const eCode = summary.E || 'M';
    const bCode = summary.B || 'M';

    const sTitle = getC(`beyond.beyond_test.results.S.${sCode}.title`) || sCode;
    const eTitle = getC(`beyond.beyond_test.results.E.${eCode}.title`) || eCode;
    const bTitle = getC(`beyond.beyond_test.results.B.${bCode}.title`) || bCode;

    const sDesc = extractShortDesc(getC(`beyond.beyond_test.results.S.${sCode}.desc`));
    const eDesc = extractShortDesc(getC(`beyond.beyond_test.results.E.${eCode}.desc`));
    const bDesc = extractShortDesc(getC(`beyond.beyond_test.results.B.${bCode}.desc`));

    const axisItems = [
        { icon: '🌌', label: t('mypage.results.beyond.emotion_style'), title: sTitle, desc: sDesc },
        { icon: '🤝', label: t('mypage.results.beyond.relationship_distance'), title: eTitle, desc: eDesc },
        { icon: '💎', label: t('mypage.results.beyond.love_belief'), title: bTitle, desc: bDesc },
    ];

    return (
        <div style={styles.beyondContainer}>
            <div style={styles.beyondGalaxy}>
                {/* 중앙 오브 */}
                <div style={styles.beyondCenter}>
                    <div style={styles.beyondOrbMain}>
                        <span style={styles.beyondTypeLabel}>IDEAL WORLD</span>
                        <span style={styles.beyondTypeCode}>{sCode}{eCode}{bCode}</span>
                    </div>
                </div>

                {/* 각 축 카드: 라벨 + 타이틀 + 요약 설명 */}
                <div style={styles.beyondOrbsGrid}>
                    {axisItems.map((item) => (
                        <div key={item.label} style={styles.beyondOrbSmall}>
                            <div style={styles.orbIcon}>{item.icon}</div>
                            <div style={{ textAlign: 'left', flex: 1 }}>
                                <div style={styles.orbAxis}>{item.label}</div>
                                <div style={styles.orbTitle}>{item.title}</div>
                                {item.desc ? (
                                    <div style={{
                                        fontSize: '0.75rem',
                                        color: '#94a3b8',
                                        marginTop: '0.2rem',
                                        lineHeight: 1.4,
                                    }}>
                                        {item.desc}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <DateStamp value={beyond?.created_at} />
        </div>
    );
};

const MyPage = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { getC } = useContent(['beyond', 'love', 'chugumi']);

    const {
        loading,
        loggedIn,
        stats,
        entitlements,
        faceResults,
        chemistryResults,
        guestData
    } = useMyPageDashboard();

    const latestFace = useMemo(() => (
        Array.isArray(faceResults) && faceResults.length > 0 ? faceResults[0] : null
    ), [faceResults]);
    const guestHasPending = Boolean(guestData?.hasPending);
    const chemistryList = useMemo(() => (
        Array.isArray(chemistryResults) ? chemistryResults : []
    ), [chemistryResults]);

    // Quest Completion Status
    const questStatus = useMemo(() => {
        const isFaceDone = !!latestFace;
        const isChugumiDone = !!stats?.chugumi;
        const isMegaDone = !!stats?.mega;
        const isBeyondDone = !!stats?.beyond;

        const count = [isFaceDone, isChugumiDone, isMegaDone, isBeyondDone].filter(Boolean).length;

        return {
            face: isFaceDone,
            chugumi: isChugumiDone,
            mega: isMegaDone,
            beyond: isBeyondDone,
            count,
            total: 4,
            isComplete: count === 4
        };
    }, [latestFace, stats]);

    const megaCardState = getMyPageCardState({
        hasResult: !!stats?.mega,
        questComplete: !!stats?.mega,
        premiumRoute: '/premium',
        premiumUnlocked: Boolean(entitlements?.mega_premium),
    });
    const hasInsideResults = Boolean(stats?.mega && stats?.beyond);
    const megaScoreSeb = stats?.mega?.summary?.score_seb || {};
    const megaStatusCode = useMemo(() => (
        ['S', 'E', 'F']
            .map((key) => {
                const score = Number(megaScoreSeb[key]);
                if (!Number.isFinite(score)) return 'M';
                if (score >= 70) return 'H';
                if (score >= 40) return 'M';
                return 'L';
            })
            .join('')
    ), [megaScoreSeb]);

    // --- MEGA Labels & Names ---
    const megaLabelName = useMemo(() => {
        const megaResultItem = SEB_TEST_DATA.results.find((r) => r.type_code === megaStatusCode);
        const translatedTitleRaw = getC(`love.mega_test.results.${megaStatusCode}.name`);
        const translatedTitle = (
            translatedTitleRaw &&
            typeof translatedTitleRaw === 'string' &&
            !translatedTitleRaw.startsWith('[[missing:')
        )
            ? translatedTitleRaw
            : null;

        return translatedTitle || megaResultItem?.type_code || megaStatusCode;
    }, [getC, megaStatusCode]);

    const chemistryCards = useMemo(() => (
        chemistryList.map((res, index) => {
            const summary = res?.summary || {};
            const payload = res?.payload || {};
            const localized = localizeChemistryResult({
                ...payload,
                rank: payload.rank || summary.rank || res.result_label,
                result_label: res.result_label,
            }, t);
            const scores = payload?.scores || summary?.scores || {};
            const magnet = getChemistryLevel(scores?.magnet, 45);
            const mirror = getChemistryLevel(scores?.mirror, 50);

            return {
                id: res?.id || `chemistry-${res?.created_at || index}`,
                navigateState: payload || null,
                nameA: payload?.user_names?.a || summary?.user_names?.a || 'A',
                nameB: payload?.user_names?.b || summary?.user_names?.b || 'B',
                title: localized?.title || '',
                createdAtLabel: formatResultDate(res?.created_at),
                chemistryScore: summary?.chemistry_score,
                badges: [
                    magnet ? { key: 'magnet', metric: 'MAGNET', ...magnet } : null,
                    mirror ? { key: 'mirror', metric: 'MIRROR', ...mirror } : null,
                ].filter(Boolean),
            };
        })
    ), [chemistryList, t, i18n.resolvedLanguage]);

    if (loading) return <div style={styles.container}>{t('common.loading')}</div>;

    // --- Guest Mode Logic ---
    if (!loggedIn) {
        return (
            <div style={styles.container}>
                <div style={styles.content}>
                    <h1 style={styles.title}>{t('mypage.guest_title')}</h1>
                    {guestHasPending ? (
                        <div style={styles.guestBanner}>
                            <h2 style={styles.guestTitle}>{t('mypage.guest_banner.title')}</h2>
                            <p style={styles.guestText}>{t('mypage.guest_banner.desc_2')}</p>
                            <button
                                onClick={() => navigate('/login', { state: { from: { pathname: '/mypage' } } })}
                                style={styles.loginButton}
                            >
                                {t('mypage.guest_banner.button')}
                            </button>
                        </div>
                    ) : (
                        <div style={{ ...styles.section, textAlign: 'center' }}>
                            <p>{t('common.login_required')}</p>
                            <button onClick={() => navigate('/login')} style={styles.loginButton}>{t('common.login')}</button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                <h1 style={styles.title}>{t('mypage.title')}</h1>

                {/* 1. Quest Progress Section */}
                <section style={styles.questSection}>
                    <div style={styles.questHeader}>
                        <h2 style={styles.questTitle}>{t('mypage.quest.title')}</h2>
                        <span style={styles.questCount}>{t('mypage.quest.progress', { count: questStatus.count, total: questStatus.total })}</span>
                    </div>
                    <div style={styles.progressBarBg}>
                        <div style={{ ...styles.progressBarFill, width: `${(questStatus.count / questStatus.total) * 100}%` }} />
                    </div>
                    <p style={styles.questDesc}>
                        <Trans i18nKey="mypage.quest.desc" />
                    </p>
                </section>

                {/* 2. Unlock Section */}
                <section style={styles.unlockSection}>
                    {questStatus.isComplete ? (
                        <div style={styles.unlockedBox}>
                            <div style={styles.icontWrapper}>🎁</div>
                            <h3>{t('mypage.unlock.complete.title')}</h3>
                            <p><Trans i18nKey="mypage.unlock.complete.desc" /></p>
                            <button onClick={() => navigate('/premium')} style={styles.premiumButton}>
                                {t('mypage.unlock.complete.button')}
                            </button>
                        </div>
                    ) : (
                        <div style={styles.lockedBox}>
                            <div style={styles.lockIcon}>🔒</div>
                            <h3>{t('mypage.unlock.locked.title')}</h3>
                            <p><Trans i18nKey="mypage.unlock.locked.desc" /></p>
                        </div>
                    )}
                </section>

                {/* 3. Appearance Analysis Category */}
                <section style={styles.section}>
                    <div style={styles.categoryHeader}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={styles.sectionTitle}>{t('home.category.appearance.title')}</h2>
                                <p style={styles.categoryDesc}>{t('home.category.appearance.description')}</p>
                            </div>
                            <button
                                onClick={() => navigate('/info')}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.4rem'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                }}
                            >
                                <span>📸</span> {t('mypage.results.face.start_analysis')}
                            </button>
                        </div>
                    </div>
                    <StatusGrid>
                        {/* Face Analysis Card */}
                        <ReportCard
                            title={t('mypage.report_card.face')}
                            detailUrl={latestFace?.id ? `/mypage/face-results/${latestFace.id}` : null}
                            empty={!latestFace}
                        >
                            {latestFace ? (
                                <FaceAnalysisCard faceResult={latestFace} />
                            ) : (
                                <EmptyState text={t('mypage.empty_state.face')} linkUrl="/info" buttonText={t('common.start_analysis')} />
                            )}
                        </ReportCard>

                        {/* Chugumi Analysis Card */}
                        <ReportCard
                            title={t('mypage.report_card.chugumi')}
                            detailUrl={stats?.chugumi?.id ? `/chugumi-test/result/${stats.chugumi.id}` : null}
                            empty={!stats?.chugumi}
                        >
                            {stats?.chugumi ? (
                                <div style={{ textAlign: 'center', width: '100%' }}>
                                    <div style={{ ...styles.resultKey, fontSize: '1.4rem', color: '#60a5fa', marginBottom: '1rem' }}>
                                        {getC(`chugumi.results.${stats.chugumi.result_key}.title`) || stats.chugumi.result_key || stats.chugumi.result_label}
                                    </div>
                                    <ChugumiMatrix
                                        vibeScore={stats.chugumi.scores?.vibe || 24}
                                        identityScore={stats.chugumi.scores?.identity || 24}
                                    />
                                    <DateStamp value={stats.chugumi.created_at} />
                                </div>
                            ) : (
                                <EmptyState text={t('mypage.empty_state.chugumi')} linkUrl="/chugumi-test" buttonText={t('common.start_test')} />
                            )}
                        </ReportCard>
                    </StatusGrid>
                </section>

                {/* 4. Inner Analysis Category */}
                <section style={styles.section}>
                    <div style={styles.categoryHeader}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={styles.sectionTitle}>{t('home.category.inner.title')}</h2>
                                <p style={styles.categoryDesc}>{t('home.category.inner.description')}</p>
                            </div>
                            <button
                                onClick={() => {
                                    navigate('/inside-report');
                                }}
                                style={{
                                    background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.6rem 1.2rem',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    boxShadow: '0 4px 6px rgba(168, 85, 247, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                <span>🛸</span> {t('mypage.results.inner.start_analysis')}
                            </button>
                        </div>
                    </div>
                    <StatusGrid>
                        {/* Mega Synergy Card */}
                        <ReportCard
                            title={t('mypage.report_card.mega')}
                            detailUrl={stats?.mega?.id ? `/mega-test/result/${stats.mega.id}` : null}
                            empty={!stats?.mega}
                            highlight
                        >
                            {stats?.mega ? (
                                <div style={styles.megaContent}>
                                    <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.8rem' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <span style={styles.megaType}>{megaStatusCode}</span>
                                            <span style={{ fontSize: '0.7rem', color: '#60a5fa', opacity: 0.8, letterSpacing: '1px' }}>TYPE CODE</span>
                                        </div>
                                        <div style={{ width: '1px', height: '30px', background: 'rgba(255,255,255,0.1)' }} />
                                        <span style={styles.megaLabel}>
                                            {megaLabelName}
                                        </span>
                                    </div>
                                    <ScoreRadar
                                        scores={{
                                            max: 100,
                                            axes: [
                                                { key: 'S', label: `${t('mypage.results.mega.stability')}(${megaStatusCode[0]})`, score: Number(megaScoreSeb.S) || 0 },
                                                { key: 'E', label: `${t('mypage.results.mega.empathy')}(${megaStatusCode[1]})`, score: Number(megaScoreSeb.E) || 0 },
                                                { key: 'F', label: `${t('mypage.results.mega.trust')}(${megaStatusCode[2]})`, score: Number(megaScoreSeb.F) || 0 },
                                            ]
                                        }}
                                        title={t('mypage.results.mega.score_title')}
                                    />
                                    <DateStamp value={stats.mega.created_at} />
                                    {megaCardState.showPremiumCta && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); navigate(megaCardState.premiumRoute); }}
                                            className="card-premium-button"
                                            style={styles.cardPremiumButton}
                                        >
                                            👑 {t('mypage.unlock.complete.button')}
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <EmptyState text={t('mypage.empty_state.mega')} linkUrl="/mega-test" buttonText={t('common.start_test')} />
                            )}
                        </ReportCard>

                        {/* Beyond Test Card - Replaces Love Test */}
                        <ReportCard
                            title={t('mypage.results.beyond.title')}
                            detailUrl={stats?.beyond?.id ? `/beyond-test/result/${stats.beyond.id}` : null}
                            empty={!stats?.beyond}
                            highlight
                        >
                            {stats?.beyond ? (
                                <BeyondCard
                                    beyond={stats.beyond}
                                    getC={getC}
                                    styles={styles}
                                    t={t}
                                />
                            ) : (
                                <EmptyState text={t('mypage.results.beyond.empty')} linkUrl="/beyond-test" buttonText={t('mypage.results.start_test')} />
                            )}
                        </ReportCard>
                    </StatusGrid>
                    {hasInsideResults ? (
                        <div style={styles.innerReportBanner}>
                            <div>
                                <div style={styles.innerReportBannerLabel}>INNER ANALYSIS</div>
                                <h3 style={styles.innerReportBannerTitle}>{t('mypage.results.inner.available_title')}</h3>
                                <p style={styles.innerReportBannerText}>
                                    {t('mypage.results.inner.available_description')}
                                </p>
                            </div>
                            <div style={styles.innerReportBannerActions}>
                                <button
                                    onClick={() => navigate('/inside-report')}
                                    style={styles.innerReportPrimaryButton}
                                >
                                    {t('mypage.results.inner.view_analysis')}
                                </button>
                                <button
                                    onClick={() => navigate('/inside-report?mode=love')}
                                    style={styles.innerReportSecondaryButton}
                                >
                                    {t('mypage.results.inner.view_love_analysis')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div style={styles.innerReportLockedBanner}>
                            <div>
                                <div style={styles.innerReportBannerLabel}>INNER ANALYSIS</div>
                                <h3 style={styles.innerReportBannerTitle}>{t('mypage.results.inner.locked_title')}</h3>
                                <p style={styles.innerReportBannerText}>
                                    {t('mypage.results.inner.locked_description')}
                                </p>
                            </div>
                        </div>
                    )}
                </section>

                {/* 5. Relationship Library Category */}
                <section style={styles.section}>
                    <div style={styles.categoryHeader}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={styles.sectionTitle}>{t('mypage.results.chemistry.library_title')}</h2>
                                <p style={styles.categoryDesc}>{t('mypage.results.chemistry.library_description')}</p>
                            </div>
                            <button
                                onClick={() => navigate('/chemistry-test')}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.4rem'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                }}
                            >
                                <span>➕</span> {t('mypage.results.chemistry.new_analysis')}
                            </button>
                        </div>
                    </div>
                    {chemistryCards.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                            {chemistryCards.map((card) => (
                                <div
                                    key={card.id}
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(255,107,138,0.06) 0%, rgba(129,140,248,0.06) 100%)',
                                        borderRadius: '20px',
                                        padding: '1.5rem',
                                        border: '1px solid rgba(255,107,138,0.2)',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}
                                    onClick={() => navigate('/chemistry-test/result', { state: { data: card.navigateState } })}
                                    onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 30px rgba(255,107,138,0.15)'; }}
                                    onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                        <span style={{ fontSize: '15px', fontWeight: '700', color: '#ff6b8a' }}>{card.nameA}</span>
                                        <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>×</span>
                                        <span style={{ fontSize: '15px', fontWeight: '700', color: '#818cf8' }}>{card.nameB}</span>
                                        {card.createdAtLabel ? (
                                            <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
                                                {card.createdAtLabel}
                                            </span>
                                        ) : null}
                                    </div>
                                    {card.title ? (
                                        <div style={{ fontSize: '13px', fontWeight: '700', color: 'rgba(255,255,255,0.7)', marginBottom: '14px', lineHeight: '1.4' }}>
                                            {card.title}
                                        </div>
                                    ) : null}
                                    {/* 레벨 배지 행 */}
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
                                        {card.badges.map((badge) => (
                                            <div
                                                key={badge.key}
                                                style={{ flex: 1, background: `${badge.color}18`, border: `1px solid ${badge.color}40`, borderRadius: '10px', padding: '8px 10px', textAlign: 'center' }}
                                            >
                                                <div style={{ fontSize: '13px', fontWeight: '800', color: badge.color, letterSpacing: '1.5px' }}>{badge.label}</div>
                                                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.5px', marginTop: '2px' }}>{badge.metric}</div>
                                            </div>
                                        ))}
                                        <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                                            {t('mypage.results.view')} →
                                        </div>
                                    </div>
                                    {/* 종합 점수 */}
                                    {card.chemistryScore !== undefined && card.chemistryScore !== null && (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            paddingTop: '10px',
                                            borderTop: '1px solid rgba(255,255,255,0.06)',
                                        }}>
                                            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.5px' }}>{t('mypage.results.chemistry.total_score')}</span>
                                            <span style={{
                                                fontSize: '14px',
                                                fontWeight: '800',
                                                background: 'linear-gradient(135deg, #ff6b8a, #818cf8)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text',
                                            }}>
                                                {card.chemistryScore}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState text={t('mypage.results.chemistry.empty')} linkUrl="/chemistry-test" buttonText={t('mypage.results.chemistry.start')} />
                    )}
                </section>

            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        color: 'white',
        padding: '2rem 1rem',
        display: 'flex',
        justifyContent: 'center',
    },
    content: {
        width: '100%',
        maxWidth: '1000px',
    },
    title: {
        fontSize: '2rem',
        fontWeight: 'bold',
        marginBottom: '2rem',
        textAlign: 'center',
        background: 'linear-gradient(to right, #60a5fa, #a78bfa)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
    questSection: {
        background: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '20px',
        padding: '1.5rem',
        marginBottom: '2rem',
        border: '1px solid rgba(255,255,255,0.1)',
    },
    questHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.8rem',
    },
    questTitle: {
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: '#fff',
        margin: 0,
    },
    questCount: {
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#60a5fa',
    },
    progressBarBg: {
        width: '100%',
        height: '10px',
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: '5px',
        overflow: 'hidden',
        marginBottom: '0.8rem',
    },
    progressBarFill: {
        height: '100%',
        backgroundColor: '#60a5fa',
        background: 'linear-gradient(90deg, #60a5fa, #3b82f6)',
        borderRadius: '5px',
        transition: 'width 0.5s ease-in-out',
    },
    questDesc: {
        fontSize: '0.9rem',
        color: '#94a3b8',
        margin: 0,
    },
    unlockSection: {
        marginBottom: '3rem',
    },
    unlockedBox: {
        background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1), rgba(245, 158, 11, 0.1))',
        border: '1px solid rgba(251, 191, 36, 0.3)',
        borderRadius: '20px',
        padding: '2rem',
        textAlign: 'center',
        animation: 'softGlow 3s ease-in-out infinite',
    },
    lockedBox: {
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px dashed rgba(255, 255, 255, 0.2)',
        borderRadius: '20px',
        padding: '2rem',
        textAlign: 'center',
    },
    icontWrapper: {
        fontSize: '3rem',
        marginBottom: '0.5rem',
    },
    lockIcon: {
        fontSize: '2.5rem',
        marginBottom: '0.5rem',
        opacity: 0.5,
    },
    premiumButton: {
        background: 'linear-gradient(90deg, #fbbf24, #f59e0b)',
        color: '#000',
        border: 'none',
        padding: '1rem 2rem',
        borderRadius: '12px',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        marginTop: '1rem',
        boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
        transition: 'transform 0.2s',
    },
    section: {
        marginBottom: '3rem',
    },
    categoryHeader: {
        marginBottom: '1.5rem',
        borderLeft: '4px solid #60a5fa',
        paddingLeft: '1rem',
    },
    sectionTitle: {
        fontSize: '1.5rem',
        marginBottom: '0.5rem',
        color: '#fff',
    },
    categoryDesc: {
        color: '#94a3b8',
        fontSize: '0.95rem',
        margin: 0,
    },
    // Mega Specific
    megaContent: {
        textAlign: 'center',
    },
    megaType: {
        fontSize: '1.5rem',
        fontWeight: '900',
        color: '#60a5fa',
        marginRight: '0.5rem',
    },
    megaLabel: {
        fontSize: '1.1rem',
        color: '#cbd5e1',
    },
    guestBanner: {
        textAlign: 'center',
        padding: '2rem',
        background: 'rgba(59, 130, 246, 0.15)',
        borderRadius: '16px',
        border: '1px solid rgba(59, 130, 246, 0.3)',
    },
    guestTitle: {
        fontSize: '1.5rem',
        marginBottom: '1rem',
        color: '#fff',
    },
    guestText: {
        color: '#cbd5e1',
        marginBottom: '1.5rem',
    },
    loginButton: {
        padding: '0.8rem 1.5rem',
        background: '#3b82f6',
        color: '#fff',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: 'bold',
    },
    resultKey: {
        fontWeight: 'bold',
        fontSize: '1.1rem'
    },
    resultDate: {
        marginTop: '1rem',
        textAlign: 'right',
        fontSize: '0.8rem',
        color: '#64748b',
        fontWeight: 600,
    },
    cardPremiumButton: {
        width: '100%',
        marginTop: '1.5rem',
        padding: '0.8rem',
        background: 'linear-gradient(90deg, #fbbf24, #f59e0b)',
        color: '#000',
        border: 'none',
        borderRadius: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
        transition: 'all 0.2s',
    },
    innerReportBanner: {
        marginTop: '1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1.5rem',
        padding: '1.5rem',
        borderRadius: '20px',
        background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.16), rgba(245, 158, 11, 0.12))',
        border: '1px solid rgba(251, 191, 36, 0.35)',
        boxShadow: '0 10px 30px rgba(245, 158, 11, 0.08)',
    },
    innerReportLockedBanner: {
        marginTop: '1.5rem',
        padding: '1.5rem',
        borderRadius: '20px',
        background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.08), rgba(245, 158, 11, 0.05))',
        border: '1px dashed rgba(251, 191, 36, 0.28)',
    },
    innerReportBannerLabel: {
        fontSize: '0.75rem',
        fontWeight: '800',
        letterSpacing: '0.14em',
        color: '#fbbf24',
        marginBottom: '0.5rem',
    },
    innerReportBannerTitle: {
        margin: 0,
        fontSize: '1.2rem',
        color: '#fff7d6',
    },
    innerReportBannerText: {
        margin: '0.6rem 0 0',
        color: '#fde68a',
        lineHeight: 1.6,
        maxWidth: '620px',
        fontSize: '0.95rem',
    },
    innerReportBannerActions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        justifyContent: 'flex-end',
    },
    innerReportPrimaryButton: {
        padding: '0.9rem 1.2rem',
        borderRadius: '12px',
        border: 'none',
        background: '#fbbf24',
        color: '#111827',
        fontWeight: '800',
        cursor: 'pointer',
        boxShadow: '0 6px 18px rgba(251, 191, 36, 0.22)',
    },
    innerReportSecondaryButton: {
        padding: '0.9rem 1.2rem',
        borderRadius: '12px',
        border: '1px solid rgba(255, 248, 220, 0.35)',
        background: 'rgba(255, 255, 255, 0.08)',
        color: '#fff8dc',
        fontWeight: '800',
        cursor: 'pointer',
    },
    // Beyond Visual Styles
    beyondContainer: {
        padding: '1.5rem 1rem',
        textAlign: 'center',
    },
    beyondGalaxy: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2rem',
    },
    beyondCenter: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    beyondOrbMain: {
        width: '140px',
        height: '140px',
        borderRadius: '50%',
        background: 'radial-gradient(circle at 30% 30%, #a78bfa 0%, #6366f1 50%, #1e1b4b 100%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: '0 0 30px rgba(99, 102, 241, 0.4), inset 0 0 20px rgba(255,255,255,0.2)',
        animation: 'float 4s ease-in-out infinite',
    },
    beyondTypeLabel: {
        fontSize: '0.6rem',
        letterSpacing: '2px',
        color: 'rgba(255,255,255,0.7)',
        marginBottom: '0.2rem',
    },
    beyondTypeCode: {
        fontSize: '2rem',
        fontWeight: '900',
        color: '#fff',
        letterSpacing: '4px',
    },
    beyondOrbsGrid: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.8rem',
        width: '100%',
    },
    beyondOrbSmall: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '0.8rem 1rem',
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '16px',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(5px)',
        transition: 'transform 0.2s',
    },
    orbIcon: {
        fontSize: '1.5rem',
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '12px',
    },
    orbAxis: {
        fontSize: '0.7rem',
        color: '#94a3b8',
        marginBottom: '0.1rem',
    },
    orbTitle: {
        fontSize: '0.95rem',
        fontWeight: 'bold',
        color: '#e2e8f0',
    }
};

// Animation injection
const styleSheet = document.createElement("style");
styleSheet.innerText = `
    @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
    }
    @keyframes softGlow {
        0% { border-color: rgba(251, 191, 36, 0.3); box-shadow: 0 0 10px rgba(251, 191, 36, 0.05); }
        50% { border-color: rgba(251, 191, 36, 0.7); box-shadow: 0 0 25px rgba(251, 191, 36, 0.15); }
        100% { border-color: rgba(251, 191, 36, 0.3); box-shadow: 0 0 10px rgba(251, 191, 36, 0.05); }
    }
    .card-premium-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4) !important;
        filter: brightness(1.1);
    }
    .card-premium-button:active {
        transform: translateY(0);
    }
`;
document.head.appendChild(styleSheet);

export default MyPage;
